import React, {
  useState,
  useCallback,
  useEffect,
  useReducer,
  useRef,
} from "react";
import { motion, AnimatePresence } from "framer-motion";
import DivTag from "../Components/DivComponent/DivTag";
import Loader from "../../components/Loader/Loader";
import {
  RegEmployeeServiceReport,
  RegEmployeeEdit,
  RegEmployeeUpdate,
  FetchEmployeePermissions,
} from "../../services/apiService";
import { ToastContainer } from "react-toastify";
import { showSuccessToast, showErrorToast } from "../../utils/toastUtils";
import { getAdminProfile } from "../../services/authService";
import { Trash2 } from "lucide-react";

// Modal Component
const EmployeeModal = ({ isOpen, onClose, employee, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [allRoutes, setAllRoutes] = useState([]);
  const [formData, setFormData] = useState({
    user_id: "",
    company_name: "",
    name: "",
    email_address: "",
    mobile_number: "",
    password: "",
    pin_code: "",
    landmark_address: "",
    post_office: "",
    district: "",
    state: "",
    dob: "",
    aadhar_number: "",
    pan_number: "",
  });
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [errors, setErrors] = useState({});

  const [allAdminRoutes, setAllAdminRoutes] = useState([]);
  // const [allEmployeePermission, setAllEmployeePermission] = useState(null);
  const [adminId, setAdminId] = useState(null);

  const [profilePic, setProfilePic] = useState("");
  const [profilePreview, setProfilePreview] = useState("");

  const fileRef = useRef();

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await getAdminProfile();
        if (res?.data?.user_id === "ADM0001") {
          setAdminId(res?.data?.user_id);
        }
      } catch (error) {
        console.error("Error fetching mobile number", error);
      }
    };
    fetchProfile();
  }, []);

  useEffect(() => {
    if (isOpen && employee) {
      loadEmployeeData();
    }
  }, [isOpen, employee]);

  const loadEmployeeData = async () => {
    setLoading(true);
    try {
      const response = await RegEmployeeEdit(employee.user_id);

      console.log("RegEmployeeEdit Response:", response);

      if (response?.status?.code === 200 && response?.data?.user) {
        const userData = response.data.user;
        console.log("User data from RegEmployeeEdit:", userData);

        setFormData({
          user_id: userData?.user_id,
          name: userData.name || "",
          email_address: userData.email_address || "",
          mobile_number: userData.mobile_number || "",
          password: "",
          pin_code: userData.pin_code || "",
          landmark_address: userData.landmark_address || "",
          post_office: userData.post_office || "",
          district: userData.district || "",
          state: userData.state || "",
          dob: userData.dob ? userData.dob.split(" ")[0] : "",
          aadhar_number: userData.aadhar_number || "",
          pan_number: userData.pan_number || "",
        });
      } else {
        throw new Error(
          response?.data?.message || "Failed to load employee data",
        );
      }
    } catch (error) {
      console.error("Load employee error:", error);
      showErrorToast(error.message || "Error loading employee data");
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const handleChange = useCallback(
    (e) => {
      const { name, value } = e.target;
      setFormData((prev) => ({ ...prev, [name]: value }));
      if (errors[name]) {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[name];
          return newErrors;
        });
      }
    },
    [errors],
  );

  const handlePermissionToggle = useCallback((routePath) => {
    setSelectedPermissions((prev) =>
      prev.includes(routePath)
        ? prev.filter((path) => path !== routePath)
        : [...prev, routePath],
    );
  }, []);

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Name is required";
    // if (!formData.company_name.trim()) newErrors.company_name = "Name is required";
    if (!formData.user_id.trim()) newErrors.user_id = "User Id is required";
    if (!formData.email_address.trim()) {
      newErrors.email_address = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email_address)) {
      newErrors.email_address = "Invalid email format";
    }
    if (!formData.mobile_number.trim()) {
      newErrors.mobile_number = "Mobile is required";
    } else if (!/^\d{10}$/.test(formData.mobile_number)) {
      newErrors.mobile_number = "Invalid 10-digit mobile";
    }
    if (formData.password && formData.password.length < 6) {
      newErrors.password = "Password must be 6+ characters";
    }
    if (formData.aadhar_number && !/^\d{12}$/.test(formData.aadhar_number)) {
      newErrors.aadhar_number = "Invalid Aadhar (12 digits)";
    }
    if (
      formData.pan_number &&
      !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(formData.pan_number.toUpperCase())
    ) {
      newErrors.pan_number = "Invalid PAN format";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      showErrorToast("Please fix validation errors");
      return;
    }

    // Permission validation removed - allow unchecking permissions
    // if (selectedPermissions.length === 0 && adminId) {
    //   showErrorToast("Select at least one permission");
    //   return;
    // }

    setLoading(true);

    try {
      // Build permissions array with ALL routes and their status (true for selected, false for unselected)
      const permissions = allAdminRoutes.map((route) => ({
        url: route.url,
        status: selectedPermissions.includes(route.url) ? 1 : 0,
      }));

      // Prepare user data - required fields
      const userData = {
        name: formData.name.trim(),
        email_address: formData.email_address.trim(),
        mobile_number: formData.mobile_number.trim(),
      };

      // Add optional fields only if they have values
      if (formData.user_id) userData.user_id = formData.user_id;
      if (formData.company_name) userData.brand_name = formData.company_name;
      if (formData.password) userData.password = formData.password;
      if (formData.pin_code) userData.pin_code = formData.pin_code.trim();
      if (formData.landmark_address)
        userData.landmark_address = formData.landmark_address.trim();
      if (formData.post_office)
        userData.post_office = formData.post_office.trim();
      if (formData.district) userData.district = formData.district.trim();
      if (formData.state) userData.state = formData.state.trim();
      if (formData.dob) userData.dob = formData.dob;
      if (formData.aadhar_number)
        userData.aadhar_number = formData.aadhar_number;
      if (formData.pan_number)
        userData.pan_number = formData.pan_number.toUpperCase();

      // const payload = {
      //   user: userData,
      // };

      // if (adminId) payload.permissions = permissions;

      const response = await RegEmployeeUpdate(
        employee.user_id,
        userData,
        permissions,
        profilePic,
      );

      if (
        response?.status?.code === 200 ||
        response?.status?.code === 201 ||
        response?.data?.status === 200 ||
        response?.data?.success === true
      ) {
        showSuccessToast(
          response?.data?.message || "Employee updated successfully",
        );
        onSuccess();
        onClose();
      } else {
        throw new Error(
          response?.data?.message || response?.message || "Update failed",
        );
      }
    } catch (error) {
      console.error("Update error:", error);

      let errorMsg = "Error updating employee";

      if (error?.response?.data?.message) {
        errorMsg = error.response.data.message;
      } else if (error?.response?.data?.error) {
        errorMsg = error.response.data.error;
      } else if (error?.response?.data?.errors) {
        const validationErrors = error.response.data.errors;
        errorMsg = Object.values(validationErrors).flat().join(", ");
      } else if (error?.message) {
        errorMsg = error.message;
      }

      if (errorMsg.includes("could not be found") || errorMsg.includes("404")) {
        errorMsg = "API endpoint not found. Please contact administrator.";
      }

      showErrorToast(errorMsg);
    } finally {
      setLoading(false);
    }
  };
  let isLatest = true;

  const requestIdRef = useRef(0);

  useEffect(() => {
    if (!employee?.user_id) return;

    const fetchPermissions = async () => {
      const requestId = ++requestIdRef.current;

      try {
        const res = await FetchEmployeePermissions({
          user_id: employee.user_id,
        });

        if (requestId !== requestIdRef.current) return;

        const routes = res?.data?.permissions ?? [];
        console.log("Routes from FetchEmployeePermissions:", routes);

        setAllAdminRoutes(routes);

        const filteredPermissions = routes
          .filter((item) => item.status)
          .map((item) => item.url);
        console.log("Filtered permissions:", filteredPermissions);

        setSelectedPermissions(filteredPermissions);
      } catch (error) {
        console.log(error);
      }
    };

    fetchPermissions();
  }, [employee?.user_id]);

  useEffect(() => {
    console.log("selected permissions++++++++", selectedPermissions);
  }, [selectedPermissions]);

  useEffect(() => {
    console.log("formData", formData);
  }, [formData]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen px-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-60"
            onClick={onClose}
          />

          <motion.div
            initial={{ scale: 0.95, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.95, opacity: 0, y: 20 }}
            className="relative bg-gray-800 rounded-lg shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-y-auto"
          >
            {loading && <Loader />}

            {/* Header */}
            <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 rounded-t-lg z-10 shadow-md">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-white text-xl font-bold flex items-center gap-2">
                    <svg
                      className="w-6 h-6"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                      />
                    </svg>
                    Edit Employee
                  </h2>
                  <p className="text-blue-100 text-sm mt-1">
                    {employee?.name} • {employee?.user_id}
                  </p>
                </div>

                <div className="flex items-center justify-between gap-4">
                  {/* Preview Card */}
                  {profilePreview && (
                    <div className="flex items-center gap-4 px-4 py-2 rounded-xl bg-white/10 backdrop-blur-md border border-white/20 shadow-md">
                      {/* Image */}
                      <div className="relative">
                        <img
                          src={profilePreview}
                          alt="profilePic"
                          className="w-14 h-14 rounded-lg object-cover border border-white/20"
                        />

                        {/* Remove Button (overlay) */}
                        <button
                          onClick={() => {
                            if (fileRef.current) {
                              fileRef.current.value = "";
                            }
                            setProfilePreview("");
                          }}
                          className="absolute -top-2 -right-2 bg-red-500 hover:bg-red-600 text-white p-1.5 rounded-full shadow-md transition"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>

                      {/* Label */}
                      <div className="flex flex-col">
                        <span className="text-xs text-gray-300">
                          Profile Image
                        </span>
                        <span className="text-sm font-medium text-white">
                          Preview Ready
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Close Button */}
                  <button
                    onClick={onClose}
                    className="group p-2 rounded-full bg-white/10 hover:bg-red-500 transition-all duration-200 shadow-md"
                  >
                    <svg
                      className="w-5 h-5 text-gray-300 group-hover:text-white transition"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-6">
              {/* Personal Information */}
              <div className="mb-6">
                <h3 className="text-white text-lg font-semibold mb-3 flex items-center gap-2">
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    />
                  </svg>
                  Personal Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-gray-300 mb-1 text-sm font-medium">
                      User Id <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      name="user_id"
                      value={formData.user_id}
                      onChange={handleChange}
                      className="w-full p-2.5 border border-gray-600 bg-gray-700 text-white rounded focus:ring-2 focus:ring-blue-500 text-sm"
                      placeholder="Enter name"
                    />
                    {errors.user_id && (
                      <p className="text-red-400 text-xs mt-1">
                        {errors.user_id}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-gray-300 mb-1 text-sm font-medium">
                      Company Name
                    </label>
                    <input
                      type="text"
                      name="company_name"
                      value={formData.company_name}
                      onChange={handleChange}
                      className="w-full p-2.5 border border-gray-600 bg-gray-700 text-white rounded focus:ring-2 focus:ring-blue-500 text-sm"
                      placeholder="Enter name"
                    />
                    {errors.company_name && (
                      <p className="text-red-400 text-xs mt-1">
                        {errors.company_name}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-gray-300 mb-1 text-sm font-medium">
                      Name <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full p-2.5 border border-gray-600 bg-gray-700 text-white rounded focus:ring-2 focus:ring-blue-500 text-sm"
                      placeholder="Enter name"
                    />
                    {errors.name && (
                      <p className="text-red-400 text-xs mt-1">{errors.name}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-gray-300 mb-1 text-sm font-medium">
                      Email <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="email"
                      name="email_address"
                      value={formData.email_address}
                      onChange={handleChange}
                      className="w-full p-2.5 border border-gray-600 bg-gray-700 text-white rounded focus:ring-2 focus:ring-blue-500 text-sm"
                      placeholder="Enter email"
                    />
                    {errors.email_address && (
                      <p className="text-red-400 text-xs mt-1">
                        {errors.email_address}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-gray-300 mb-1 text-sm font-medium">
                      Mobile <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      name="mobile_number"
                      value={formData.mobile_number}
                      onChange={handleChange}
                      maxLength="10"
                      className="w-full p-2.5 border border-gray-600 bg-gray-700 text-white rounded focus:ring-2 focus:ring-blue-500 text-sm"
                      placeholder="10-digit mobile"
                    />
                    {errors.mobile_number && (
                      <p className="text-red-400 text-xs mt-1">
                        {errors.mobile_number}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-gray-300 mb-1 text-sm font-medium">
                      Profile Picture
                    </label>
                    <input
                      type="file"
                      ref={fileRef}
                      accept="image/*"
                      name="profile_picture"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (!file) return;

                        setProfilePic(file);
                        setProfilePreview(URL.createObjectURL(file));
                      }}
                      className="w-full px-2.5 py-[7px] border border-gray-600 bg-gray-700 text-white rounded focus:ring-2 focus:ring-blue-500 text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-gray-300 mb-1 text-sm font-medium">
                      Password
                    </label>
                    <input
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      className="w-full p-2.5 border border-gray-600 bg-gray-700 text-white rounded focus:ring-2 focus:ring-blue-500 text-sm"
                      placeholder="Leave blank to keep current"
                    />
                    {errors.password && (
                      <p className="text-red-400 text-xs mt-1">
                        {errors.password}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-gray-300 mb-1 text-sm font-medium">
                      Date of Birth
                    </label>
                    <input
                      type="date"
                      name="dob"
                      value={formData.dob}
                      onChange={handleChange}
                      max={new Date().toISOString().split("T")[0]}
                      className="w-full p-2.5 border border-gray-600 bg-gray-700 text-white rounded focus:ring-2 focus:ring-blue-500 text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-gray-300 mb-1 text-sm font-medium">
                      Aadhar Number
                    </label>
                    <input
                      type="text"
                      name="aadhar_number"
                      value={formData.aadhar_number}
                      onChange={handleChange}
                      maxLength="12"
                      className="w-full p-2.5 border border-gray-600 bg-gray-700 text-white rounded focus:ring-2 focus:ring-blue-500 text-sm"
                      placeholder="12 digits"
                    />
                    {errors.aadhar_number && (
                      <p className="text-red-400 text-xs mt-1">
                        {errors.aadhar_number}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-gray-300 mb-1 text-sm font-medium">
                      PAN Number
                    </label>
                    <input
                      type="text"
                      name="pan_number"
                      value={formData.pan_number}
                      onChange={handleChange}
                      maxLength="10"
                      style={{ textTransform: "uppercase" }}
                      className="w-full p-2.5 border border-gray-600 bg-gray-700 text-white rounded focus:ring-2 focus:ring-blue-500 text-sm"
                      placeholder="ABCDE1234F"
                    />
                    {errors.pan_number && (
                      <p className="text-red-400 text-xs mt-1">
                        {errors.pan_number}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Address Information */}
              <div className="mb-6">
                <h3 className="text-white text-lg font-semibold mb-3 flex items-center gap-2">
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                  Address Details
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-gray-300 mb-1 text-sm font-medium">
                      PIN Code
                    </label>
                    <input
                      type="text"
                      name="pin_code"
                      value={formData.pin_code}
                      onChange={handleChange}
                      maxLength="6"
                      className="w-full p-2.5 border border-gray-600 bg-gray-700 text-white rounded focus:ring-2 focus:ring-blue-500 text-sm"
                      placeholder="6-digit PIN"
                    />
                  </div>

                  <div>
                    <label className="block text-gray-300 mb-1 text-sm font-medium">
                      Landmark
                    </label>
                    <input
                      type="text"
                      name="landmark_address"
                      value={formData.landmark_address}
                      onChange={handleChange}
                      className="w-full p-2.5 border border-gray-600 bg-gray-700 text-white rounded focus:ring-2 focus:ring-blue-500 text-sm"
                      placeholder="Landmark"
                    />
                  </div>

                  <div>
                    <label className="block text-gray-300 mb-1 text-sm font-medium">
                      Post Office
                    </label>
                    <input
                      type="text"
                      name="post_office"
                      value={formData.post_office}
                      onChange={handleChange}
                      className="w-full p-2.5 border border-gray-600 bg-gray-700 text-white rounded focus:ring-2 focus:ring-blue-500 text-sm"
                      placeholder="Post office"
                    />
                  </div>

                  <div>
                    <label className="block text-gray-300 mb-1 text-sm font-medium">
                      District
                    </label>
                    <input
                      type="text"
                      name="district"
                      value={formData.district}
                      onChange={handleChange}
                      className="w-full p-2.5 border border-gray-600 bg-gray-700 text-white rounded focus:ring-2 focus:ring-blue-500 text-sm"
                      placeholder="District"
                    />
                  </div>

                  <div>
                    <label className="block text-gray-300 mb-1 text-sm font-medium">
                      State
                    </label>
                    <input
                      type="text"
                      name="state"
                      value={formData.state}
                      onChange={handleChange}
                      className="w-full p-2.5 border border-gray-600 bg-gray-700 text-white rounded focus:ring-2 focus:ring-blue-500 text-sm"
                      placeholder="State"
                    />
                  </div>
                </div>
              </div>

              {/* Permissions Section */}

              {adminId && (
                <div className="mb-6">
                  <h3 className="text-white text-lg font-semibold mb-3 flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                        />
                      </svg>
                      Access Permissions
                    </span>
                    <span className="text-sm bg-blue-600 px-3 py-1 rounded-full">
                      {selectedPermissions?.length} / {allAdminRoutes?.length}{" "}
                      selected
                    </span>
                  </h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 max-h-80 overflow-y-auto p-4 bg-gray-900 rounded-lg border border-gray-700">
                    {allAdminRoutes?.map((route) => (
                      <label
                        key={route.id}
                        className={`relative p-3 rounded-lg cursor-pointer transition-all duration-200 border-2 ${
                          selectedPermissions?.includes(route.url)
                            ? "bg-blue-600 border-blue-400 shadow-lg shadow-blue-500/50"
                            : "bg-gray-800 border-gray-700 hover:border-gray-600 hover:bg-gray-750"
                        }`}
                      >
                        <div className="flex items-start gap-2">
                          <input
                            type="checkbox"
                            checked={selectedPermissions?.includes(route.url)}
                            onChange={() => handlePermissionToggle(route.url)}
                            className="mt-0.5 w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                          />
                          <div className="flex-1">
                            <span
                              className={`text-sm font-medium block ${
                                selectedPermissions?.includes(route.url)
                                  ? "text-white"
                                  : "text-gray-300"
                              }`}
                            >
                              {route.route_name}
                            </span>
                            <span
                              className={`text-xs block mt-0.5 ${
                                selectedPermissions?.includes(route.url)
                                  ? "text-blue-100"
                                  : "text-gray-500"
                              }`}
                            >
                              /{route.url}
                            </span>
                          </div>
                        </div>
                        {selectedPermissions?.includes(route.url) && (
                          <div className="absolute top-1 right-1">
                            <svg
                              className="w-4 h-4 text-green-400"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </div>
                        )}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={loading}
                  className="px-6 py-2.5 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition disabled:opacity-50 flex items-center gap-2 font-medium"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition disabled:opacity-50 flex items-center gap-2 font-medium shadow-lg"
                >
                  {loading ? (
                    <>
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                          fill="none"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      Updating...
                    </>
                  ) : (
                    <>
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      Update Employee
                    </>
                  )}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      </div>
    </AnimatePresence>
  );
};

// Filter Reducer
const filterReducer = (state, action) => {
  switch (action.type) {
    case "SET_FILTER":
      return { ...state, [action.field]: action.value };
    case "RESET_FILTERS":
      return {
        from_date: "",
        to_date: "",
        aadhar_number: "",
        pan_number: "",
        mobile: "",
        user_id: "",
        name: "",
        status: "",
        total_data: 10,
      };
    default:
      return state;
  }
};

// Main Component
const RegEmployeeReport = () => {
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pagination, setPagination] = useState(null);
  const [page, setPage] = useState(1);

  const [filters, dispatchFilter] = useReducer(filterReducer, {
    from_date: "",
    to_date: "",
    aadhar_number: "",
    pan_number: "",
    mobile: "",
    user_id: "",
    name: "",
    status: "",
    total_data: 10,
  });

  const fetchEmployeeReport = useCallback(async () => {
    setLoading(true);
    try {
      const payload = {
        total_data: filters.total_data,
        ...(filters.from_date && { from_date: filters.from_date }),
        ...(filters.to_date && { to_date: filters.to_date }),
        ...(filters.aadhar_number && { aadhar_number: filters.aadhar_number }),
        ...(filters.pan_number && { pan_number: filters.pan_number }),
        ...(filters.mobile && { mobile: filters.mobile }),
        ...(filters.user_id && { user_id: filters.user_id }),
        ...(filters.name && { name: filters.name }),
        ...(filters.status && { status: parseInt(filters.status) }),
      };
      payload.page = page;

      const response = await RegEmployeeServiceReport(payload);

      if (
        response?.status?.code === 200 ||
        response?.data?.status?.code === 200
      ) {
        const employeeData = response.data?.data || response.data || [];
        setEmployees(Array.isArray(employeeData) ? employeeData : []);
        setPagination(response?.pagination);
      } else {
        throw new Error(
          response?.data?.message ||
            response?.message ||
            "Failed to load employees",
        );
      }
    } catch (error) {
      console.error("Fetch error:", error);
      const errorMsg =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Error fetching employees";
      showErrorToast(errorMsg);
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  }, [filters, page]);

  useEffect(() => {
    fetchEmployeeReport();
  }, [page]);


  useEffect(() => {

    console.log("page", page);
    console.log("pagination", pagination);
    

  }, [page, pagination])

  const handleFilterChange = useCallback((e) => {
    const { name, value } = e.target;
    dispatchFilter({ type: "SET_FILTER", field: name, value });
  }, []);

  const handleSearch = useCallback(
    () => fetchEmployeeReport(),
    [fetchEmployeeReport],
  );

  const handleResetFilters = useCallback(() => {
    dispatchFilter({ type: "RESET_FILTERS" });
    setTimeout(fetchEmployeeReport, 100);
  }, [fetchEmployeeReport]);

  const handleEditClick = (employee) => {
    setSelectedEmployee(employee);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedEmployee(null);
  };

  const getStatusBadge = useCallback((statusId, statusValue) => {
    const statusMap = {
      1: { label: "Pending", color: "bg-yellow-500" },
      2: { label: "Approved", color: "bg-green-500" },
      3: { label: "Rejected", color: "bg-red-500" },
      4: { label: "Suspended", color: "bg-gray-500" },
    };
    const statusInfo = statusMap[statusId] || {
      label: statusValue || "Unknown",
      color: "bg-gray-400",
    };
    return (
      <span
        className={`px-2 py-1 text-xs text-white rounded-full ${statusInfo.color}`}
      >
        {statusInfo.label}
      </span>
    );
  }, []);

  const getKycBadge = useCallback((kycStatusId, kycStatusValue) => {
    const kycStatusMap = {
      1: { label: "Pending", color: "bg-yellow-500" },
      2: { label: "Verified", color: "bg-green-500" },
      3: { label: "Rejected", color: "bg-red-500" },
    };
    const kycInfo = kycStatusMap[kycStatusId] || {
      label: kycStatusValue || "Unknown",
      color: "bg-gray-400",
    };
    return (
      <span
        className={`px-2 py-1 text-xs text-white rounded-full ${kycInfo.color}`}
      >
        {kycInfo.label}
      </span>
    );
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, y: -50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="p-6"
    >
      {loading && <Loader />}
      <DivTag className="mt-6">
        <div className="flex justify-center mb-4">
          <h2 className="text-white text-2xl bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg px-6 py-3 shadow-lg font-bold">
            Employee Management Report
          </h2>
        </div>

        {/* Filters */}
        <div className="bg-gray-800 p-4 rounded-lg mb-4 shadow-lg border border-gray-700">
          <h3 className="text-white text-lg mb-3 font-semibold">
            Filter Employees
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-3">
            {["from_date", "to_date"].map((field) => (
              <div key={field}>
                <label className="block text-gray-300 mb-1 text-sm capitalize">
                  {field.replace("_", " ")}
                </label>
                <input
                  type="date"
                  name={field}
                  value={filters[field]}
                  onChange={handleFilterChange}
                  className="w-full p-2 border border-gray-600 bg-gray-700 text-white rounded text-sm"
                />
              </div>
            ))}
            {["mobile", "user_id", "name", "aadhar_number", "pan_number"].map(
              (field) => (
                <div key={field}>
                  <label className="block text-gray-300 mb-1 text-sm capitalize">
                    {field.replace("_", " ")}
                  </label>
                  <input
                    type="text"
                    name={field}
                    value={filters[field]}
                    onChange={handleFilterChange}
                    maxLength={
                      field === "mobile"
                        ? "10"
                        : field === "aadhar_number"
                          ? "12"
                          : field === "pan_number"
                            ? "10"
                            : undefined
                    }
                    style={
                      field === "pan_number"
                        ? { textTransform: "uppercase" }
                        : {}
                    }
                    placeholder={`Search ${field.replace("_", " ")}`}
                    className="w-full p-2 border border-gray-600 bg-gray-700 text-white rounded text-sm"
                  />
                </div>
              ),
            )}
            <div>
              <label className="block text-gray-300 mb-1 text-sm">Status</label>
              <select
                name="status"
                value={filters.status}
                onChange={handleFilterChange}
                className="w-full p-2 border border-gray-600 bg-gray-700 text-white rounded text-sm"
              >
                <option value="">All</option>
                <option value="1">Pending</option>
                <option value="2">Approved</option>
                <option value="3">Rejected</option>
                <option value="4">Suspended</option>
              </select>
            </div>
            <div>
              <label className="block text-gray-300 mb-1 text-sm">
                Records
              </label>
              <select
                name="total_data"
                value={filters.total_data}
                onChange={handleFilterChange}
                className="w-full p-2 border border-gray-600 bg-gray-700 text-white rounded text-sm"
              >
                {[10, 25, 50, 100].map((num) => (
                  <option key={num} value={num}>
                    {num}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex gap-3">
            <button
              onClick={handleSearch}
              disabled={loading}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
            >
              Search
            </button>
            <button
              onClick={handleResetFilters}
              disabled={loading}
              className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition disabled:opacity-50"
            >
              Reset
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                <tr>
                  {[
                    "User ID",
                    "Company",
                    "Name",
                    "Email",
                    "Mobile",
                    "DOB",
                    "Status",
                    "KYC",
                    "Actions",
                  ].map((header) => (
                    <th
                      key={header}
                      className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase"
                    >
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {employees.length > 0 ? (
                  employees.map((emp, idx) => (
                    <motion.tr
                      key={emp.id || idx}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: idx * 0.03 }}
                      className="hover:bg-blue-50 transition"
                    >
                      <td className="px-4 py-3 text-sm font-semibold text-gray-900">
                        {emp.user_id}
                      </td>
                      <td className="px-4 py-3 text-sm font-semibold text-gray-900">
                        {emp.brand_name || "-"}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {emp.name}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {emp.email_address || "N/A"}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {emp.mobile_number}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {emp.dob
                          ? new Date(emp.dob).toLocaleDateString("en-IN")
                          : "N/A"}
                      </td>

                      <td className="px-4 py-3">
                        {getStatusBadge(emp.status_id, emp.status?.value)}
                      </td>
                      <td className="px-4 py-3">
                        {getKycBadge(emp.kyc_status_id, emp.kyc_status?.value)}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => handleEditClick(emp)}
                          className="px-4 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-xs font-medium shadow"
                        >
                          Edit
                        </button>
                      </td>
                    </motion.tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan="9"
                      className="px-6 py-12 text-center text-gray-500"
                    >
                      No employees found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {employees.length > 0 && (
            <div className="bg-gray-50 px-6 py-3 border-t">
              <p className="text-sm text-gray-700">
                Showing <span className="font-medium">{employees.length}</span>{" "}
                employee(s)
              </p>
            </div>
          )}

          {pagination && (
            <div className="flex items-center justify-between mt-4 pb-12 px-2 text-sm text-gray-600">
              <span>
                Page {pagination.current_page} of {pagination.last_page}
              </span>

              <div className="flex gap-2">
                <button
                  disabled={!pagination.prev_page_url}
                  onClick={() => setPage((p) => p - 1)}
                  className="px-3 py-1 rounded border disabled:opacity-40"
                >
                  Prev
                </button>

                <button
                  disabled={!pagination.next_page_url}
                  onClick={() => setPage((p) => p + 1)}
                  className="px-3 py-1 rounded border disabled:opacity-40"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>

        <EmployeeModal
          key={selectedEmployee?.user_id}
          isOpen={isModalOpen}
          onClose={handleModalClose}
          employee={selectedEmployee}
          onSuccess={fetchEmployeeReport}
        />
        <ToastContainer position="top-right" autoClose={3000} />
      </DivTag>
    </motion.div>
  );
};

export default RegEmployeeReport;
